import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CheckboxEditor } from "../../../../../../react-core/Html/CheckboxEditor";
import { CurrencyCode } from "../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { UnavailabilityReason } from "../../../../../../Wrap/Constants/Enums/Domain/UnavailabilityReason";
import { Money } from "../../../../../../Wrap/Core/DataTypes/Money";
import { AccountConversion } from "../../../../../../Wrap/Models/Models/Processes/Drawdown/AccountConversion";
import { Alert } from "../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../Shared/Process/Process/ProcessComponent";
import { Table } from "../../../../../Shared/Table/Table";
import { unavailabilityReasonsMap } from "../../ConvertCappedToFlexiWizard/Resource/Content";

interface IAccountsConversionTableProps extends IProcessComponentProps {
    isReviewStep: boolean;
}

interface IAccountsConversionTableState {
    accountsAvailableForConversion: AccountConversion[];
    accountsNotAvailableForConversion: AccountConversion[];
}

export class AccountsConversionTable extends ProcessComponent<
    IAccountsConversionTableProps,
    IAccountsConversionTableState
> {
    constructor(props: any) {
        super(props);
        const { data } = this.props;
        const accountsAvailable = data.filter((account) => account.IsConvert == true);
        const accountsNotAvailable = data.filter((account) => account.IsConvert == false);
        this.state = {
            accountsAvailableForConversion: accountsAvailable,
            accountsNotAvailableForConversion: accountsNotAvailable,
        };
    }

    private renderNotAvailableAccounts() {
        const { accountsNotAvailableForConversion } = this.state;
        if (accountsNotAvailableForConversion.length == 0) {
            return <Alert alertType={AlertType.Warning} message={"All arrangements are available for conversion."} />;
        }
        const col = [
            {
                title: "Arrangement number",
                key: "AccountDetails.HierarchyId",
                render: (data: string) => {
                    const arrangementId = data.slice(9);
                    return arrangementId;
                },
            },
            {
                title: "Post-retirement arrangemnt value",
                key: "AccountDetails.TotalPostRetirementAccountValue",
                render: (data: Money) => {
                    return MoneyDisplayHelper.FromatMoneyDisplay(Math.abs(data.Amount), CurrencyCode.GBP);
                },
            },
            {
                title: "Unavailability reasons",
                key: "AccountDetails.UnavailabilityReasons",
                render: (data: Array<UnavailabilityReason>) => {
                    let reasons = "";
                    for (let i = 0; i < data.length; i++) {
                        if (i == 0) {
                            reasons = unavailabilityReasonsMap[data[i]];
                        } else {
                            reasons = reasons + ", " + unavailabilityReasonsMap[data[i]];
                        }
                    }
                    return reasons;
                },
            },
        ];
        return <Table col={col} data={accountsNotAvailableForConversion} />;
    }

    private renderAvailableAccounts() {
        let { accountsAvailableForConversion } = this.state;
        const { isReviewStep } = this.props;
        if (accountsAvailableForConversion.length == 0) {
            return <Alert alertType={AlertType.Warning} message={"No arrangements are available for conversion"} />;
        }
        const col = [
            {
                title: "Arrangement number",
                key: "AccountDetails.HierarchyId",
                render: (data: string) => {
                    const arrangementId = data.slice(9);
                    return arrangementId;
                },
            },
            {
                title: "Post-retirement arrangemnt value",
                key: "AccountDetails.TotalPostRetirementAccountValue",
                render: (data: Money) => {
                    return MoneyDisplayHelper.FromatMoneyDisplay(Math.abs(data.Amount), CurrencyCode.GBP);
                },
            },
            !isReviewStep
                ? {
                      title: "GAD maximum",
                      key: "AccountDetails.AccountValueDetails.GadMaximum.GadMaximum",
                      render: (data: Money) => {
                          return MoneyDisplayHelper.FromatMoneyDisplay(Math.abs(data.Amount), CurrencyCode.GBP);
                      },
                  }
                : null,
            !isReviewStep
                ? {
                      title: "Pre-A-day portion",
                      key: "AccountDetails.AccountValueDetails.PreADayPortion",
                      render: (data: number) => {
                          return MoneyDisplayHelper.FromatMoneyDisplay(Math.abs(data), CurrencyCode.GBP);
                      },
                  }
                : null,
            {
                title: "Inherited?",
                key: "AccountDetails.AccountValueDetails.IsInherited",
                render: (data: boolean) => {
                    return data ? "Yes" : "No";
                },
            },
            !isReviewStep
                ? {
                      title: "Convert",
                      key: "AccountDetails.IsSelected",
                      render: (data: boolean, row, index) => {
                          return (
                              <CheckboxEditor
                                  cssWrapper="mt-20 ml-20"
                                  value={data}
                                  onChange={(v) => this.onAccountSelectionChange(v, index)}
                              />
                          );
                      },
                  }
                : null,
        ];
        if (isReviewStep) {
            accountsAvailableForConversion = accountsAvailableForConversion.filter(
                (a) => a.AccountDetails.IsSelected == true
            );
        }
        return <Table col={col} data={accountsAvailableForConversion} />;
    }

    private onAccountSelectionChange(isSelected: boolean, index: number) {
        const { accountsAvailableForConversion, accountsNotAvailableForConversion } = this.state;
        accountsAvailableForConversion[index].AccountDetails.IsSelected = isSelected;
        this.props.onChange(accountsAvailableForConversion.concat(accountsNotAvailableForConversion));
    }

    public render(): JSX.Element {
        const { isReviewStep } = this.props;
        const AvailableArrangementTitle = isReviewStep
            ? "Select arrangements to convert"
            : "Arrangements available for conversion";

        return (
            <div>
                {!isReviewStep && (
                    <div className="mt-40">
                        <h2>Arrangements not available for conversion</h2>
                        {this.renderNotAvailableAccounts()}
                    </div>
                )}
                <div className="mt-40">
                    <h2>{AvailableArrangementTitle}</h2>
                    {this.renderAvailableAccounts()}
                </div>
            </div>
        );
    }
}
